import { Paper, Table as MuiTable, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel } from '@mui/material';
import { statementTablePaperSx, statementTableContainerSx, statementTableSx, statementTableHeaderSx } from './mui-table-styles';
import './Table.scss';
import { useSortableTableData } from './hooks/useSortableTableData';
import { useState, useEffect, useMemo, useRef } from 'react';
import { useTableSearch } from './hooks/useTableSearch';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faTimes, faArrowUp, faArrowDown } from '@fortawesome/free-solid-svg-icons';
import { inject } from 'mobx-react';
import useLanguageChange from '../../utils/hooks/useLanguageChange';
import DroppableRow from '../droppable-row/droppable-row';
import DraggableRow from '../draggable-row/draggable-row';
import PropTypes from 'prop-types';
import Checkbox from '../checkbox';
import LoadingSpinner from '../loading-spinner/loading-spinner';

const Table = ({
  data,
  columnNames,
  tableData,
  searchable = true,
  searchPlaceholder,
  searchControls,
  mui = false,
  renderCell,
  columnAlignments = {},
  sortable = true,
  isLoading = false,
  onRowClick = () => {},
  onExpenseDrop = () => {},
  selectableRows = false,
  selectedRows = null,
  selectableRowsCondition = () => true,
  onRowSelect = () => {},
  draggableRows = false,
  getDraggableData = (row) => row,
}) => {
  useLanguageChange(data.t);
  const { sortedData: hookSortedData, sortConfig: hookSortConfig, handleSort: hookHandleSort } = useSortableTableData(tableData);
  const sortedData = sortable ? hookSortedData : tableData;
  const sortConfig = sortable ? hookSortConfig : {};
  const handleSort = sortable ? hookHandleSort : () => {};

  const [search, setSearch] = useState('');
  // Track selected rows by a stable id (id | uuid | _id | original index)
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const isUpdatingFromPropRef = useRef(false);

  const columnKeys = typeof columnNames === 'object' && !Array.isArray(columnNames) ? Object.keys(columnNames) : columnNames;

  const filteredData = useTableSearch({
    data: tableData,
    columnNames: columnKeys,
    search,
    sortedData,
    sortConfig,
  });

  // Helper to get a stable row id
  const getRowId = (row) => row?.id ?? row?.uuid ?? row?._id ?? tableData.indexOf(row);

  const isControlled = selectedRows !== null;

  // Sync external selectedRows prop with internal state (controlled mode)
  useEffect(() => {
    if (!isControlled) return;
    isUpdatingFromPropRef.current = true;
    const newSelectedIds = new Set(selectedRows.map((r) => getRowId(r)));
    setSelectedIds(newSelectedIds);
    // Reset flag after state update completes
    setTimeout(() => {
      isUpdatingFromPropRef.current = false;
    }, 0);
  }, [selectedRows, isControlled]);

  // Clean up invalid selections when tableData changes (uncontrolled mode)
  useEffect(() => {
    if (isControlled) return;
    setSelectedIds((prev) => {
      const validIds = new Set(tableData.map((r) => getRowId(r)));
      let removed = false;
      const next = new Set();
      prev.forEach((id) => {
        if (validIds.has(id)) {
          next.add(id);
        } else {
          removed = true;
        }
      });
      return removed ? next : prev;
    });
  }, [tableData, isControlled]);

  // Select/Deselect a single row
  const toggleRowSelection = (row) => {
    const id = getRowId(row);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // Select/Deselect all currently visible (filtered) rows
  const allVisibleIds = useMemo(() => new Set(filteredData.map((r) => getRowId(r))), [filteredData]);
  const areAllVisibleSelected = allVisibleIds.size > 0 && [...allVisibleIds].every((id) => selectedIds.has(id));

  const toggleSelectAllVisible = (checked) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        allVisibleIds.forEach((id) => next.add(id));
      } else {
        allVisibleIds.forEach((id) => next.delete(id));
      }
      return next;
    });
  };

  // Notify parent AFTER selection state updates commit to avoid setState during render warnings
  useEffect(() => {
    if (!selectableRows || isUpdatingFromPropRef.current) return;
    const selectedRowsData = tableData.filter((r) => selectedIds.has(getRowId(r)));
    onRowSelect(selectedRowsData);
  }, [selectedIds, tableData, selectableRows]);

  // Swap only the rendering elements; search, sorting and selection keep their existing state.
  const Surface = mui ? Paper : 'div';
  const ScrollContainer = mui ? TableContainer : 'div';
  const TableElement = mui ? MuiTable : 'table';
  const Head = mui ? TableHead : 'thead';
  const Body = mui ? TableBody : 'tbody';
  const Row = mui ? TableRow : 'tr';
  const HeaderCell = mui ? TableCell : 'th';
  const Cell = mui ? TableCell : 'td';

  return (
    <div className='custom-table-container m-0 p-0' data-testid='table-container'>
      {searchable && (
        <div className={searchControls ? 'table-search-toolbar' : undefined}>
          <div className='search-container' data-testid='search-container'>
            <div className='search-icon'>
              <FontAwesomeIcon icon={faSearch} />
            </div>
            <input type='text' placeholder={searchPlaceholder} value={search} onChange={(e) => setSearch(e.target.value)} data-testid='search-input' />
            {search.length > 0 && (
              <button className='clear-icon' onClick={() => setSearch('')} aria-label='Clear search' data-testid='clear-search-button'>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            )}
          </div>
          {searchControls && <div className='table-search-toolbar__controls'>{searchControls}</div>}
        </div>
      )}
      <Surface {...(mui ? { elevation: 0, sx: statementTablePaperSx } : { className: 'table-wrapper' })}>
        <ScrollContainer {...(mui ? { sx: statementTableContainerSx } : {})}>
        <TableElement {...(mui ? { stickyHeader: true, sx: statementTableSx, 'aria-busy': isLoading } : { className: 'custom-table m-0 p-0 table-borderless' })} data-testid='table'>
          <Head data-testid='table-header'>
            <Row>
              {selectableRows && (
                <HeaderCell {...(mui ? { sx: { ...statementTableHeaderSx, py: 0 }, padding: 'checkbox' } : {})} style={{ width: '1%', whiteSpace: 'nowrap' }}>
                  <Checkbox
                    isChecked={areAllVisibleSelected}
                    onChange={(checked) => toggleSelectAllVisible(checked)}
                    data-testid='select-all-checkbox'
                  />
                </HeaderCell>
              )}
              {columnKeys.map((key, index) => (
                <HeaderCell
                  key={index}
                  {...(mui ? { sx: statementTableHeaderSx, align: columnAlignments[key], sortDirection: sortConfig.key === key ? sortConfig.direction : false } : {})}
                  onClick={!mui && sortable ? () => handleSort(key) : undefined}
                  style={{ cursor: sortable ? 'pointer' : 'default', userSelect: 'none' }}
                  data-testid={`column-header-${key}`}
                >
                  {mui && sortable ? (
                    <TableSortLabel active={sortConfig.key === key} direction={sortConfig.key === key ? sortConfig.direction : 'asc'} onClick={() => handleSort(key)}>
                      {data.t.read(columnNames[key])}
                    </TableSortLabel>
                  ) : data.t.read(columnNames[key])}
                  {!mui && sortable && sortConfig.key === key && sortConfig.direction && (
                    <span>
                      <FontAwesomeIcon icon={sortConfig.direction === 'asc' ? faArrowUp : faArrowDown} size='sm' />
                    </span>
                  )}
                </HeaderCell>
              ))}
            </Row>
          </Head>
          <Body data-testid='table-body'>
            {isLoading ? (
              <Row className='loading' data-testid='loading-row'>
                <Cell className='loading-cell' colSpan={(selectableRows ? 1 : 0) + columnKeys.length}>
                  <div className={mui ? 'd-flex justify-content-center' : 'loading-abs-center'}>
                    <LoadingSpinner data-testid='table-loading-spinner' />
                  </div>
                </Cell>
              </Row>
            ) : filteredData.length > 0 ? (
              filteredData.map((row, rowIndex) => {
                const id = getRowId(row);
                const checked = selectedIds.has(id);
                const rowContent = (
                  <>
                    {selectableRows && (
                      <Cell>
                        {selectableRowsCondition(row) && (
                          <Checkbox isChecked={checked} onChange={() => toggleRowSelection(row)} data-testid={`row-checkbox-${rowIndex}`} />
                        )}
                      </Cell>
                    )}
                    {columnKeys.map((colKey, colIndex) => (
                      <Cell
                        key={colIndex}
                        onClick={() => onRowClick(row)}
                        className={onRowClick !== (() => {}) ? 'clickable-row' : ''}
                        data-testid={`cell-${rowIndex}-${colKey}`}
                      >
                        {renderCell ? renderCell(row, colKey) : row[colKey]}
                      </Cell>
                    ))}
                  </>
                );

                if (draggableRows) {
                  return (
                    <DraggableRow {...(mui ? { component: TableRow, hover: true, selected: checked } : {})} key={id} rowData={getDraggableData(row)} data-testid={`table-row-${rowIndex}`}>
                      {rowContent}
                    </DraggableRow>
                  );
                }

                return (
                  <DroppableRow
                    key={id}
                    {...(mui ? { component: TableRow, hover: true, selected: checked } : {})}
                    transaction={row}
                    onExpenseDrop={(expense, transaction) => onExpenseDrop(expense, transaction)}
                    data-testid={`table-row-${rowIndex}`}
                  >
                    {rowContent}
                  </DroppableRow>
                );
              })
            ) : (
              <Row data-testid='no-data-row'>
                <Cell colSpan={(selectableRows ? 1 : 0) + columnKeys.length}>{data.t.read('no_data')}</Cell>
              </Row>
            )}
          </Body>
        </TableElement>
        </ScrollContainer>
      </Surface>
    </div>
  );
};

Table.propTypes = {
  data: PropTypes.shape({
    t: PropTypes.shape({
      read: PropTypes.func.isRequired,
    }).isRequired,
  }).isRequired,
  columnNames: PropTypes.oneOfType([PropTypes.object, PropTypes.array]).isRequired,
  tableData: PropTypes.array.isRequired,
  searchable: PropTypes.bool,
  searchPlaceholder: PropTypes.string,
  searchControls: PropTypes.node,
  mui: PropTypes.bool,
  renderCell: PropTypes.func,
  columnAlignments: PropTypes.object,
  sortable: PropTypes.bool,
  onRowClick: PropTypes.func,
  onExpenseDrop: PropTypes.func,
  selectableRows: PropTypes.bool,
  onRowSelect: PropTypes.func,
  draggableRows: PropTypes.bool,
  getDraggableData: PropTypes.func,
};

export default inject('data')(Table);
